// SPDX-License-Identifier: AGPL-3.0-only

import { gridToLongitudeLatitude } from './grid';
import type { DemGrid, InflowSource, SimulationStatistics, SolverParameters } from './types';

export interface DepthExportOptions {
  readonly thresholdMeters?: number;
  readonly maximumFeatures?: number;
}

export interface SimulationSourceRecord extends InflowSource {
  readonly startedAtModelSeconds: number;
  readonly stoppedAtModelSeconds: number | null;
  readonly injectedVolumeCubicMeters: number;
}

export interface SimulationReportContext {
  readonly solverImplementation?: string;
  readonly solverParameters?: SolverParameters;
  readonly sourceHistory?: readonly SimulationSourceRecord[];
  readonly dem?: {
    readonly encoding?: string;
    readonly sha256?: string;
    readonly source?: unknown;
  };
  readonly software?: {
    readonly name: string;
    readonly version: string;
    readonly revision?: string;
  };
}

export function exportDepthPoints(
  grid: DemGrid,
  depth: Float32Array,
  options: DepthExportOptions = {}
) {
  const threshold = options.thresholdMeters ?? 0.01;
  const requestedMaximum = options.maximumFeatures ?? 10_000;
  const maximumFeatures = Number.isFinite(requestedMaximum)
    ? Math.max(0, Math.floor(requestedMaximum))
    : 10_000;
  let wetCount = 0;
  for (const value of depth) if (value >= threshold) wetCount += 1;
  const stride =
    maximumFeatures === 0 ? 1 : Math.max(1, Math.ceil(Math.sqrt(wetCount / maximumFeatures)));
  const features: unknown[] = [];

  sampling: for (let y = 0; y < grid.height; y += stride) {
    for (let x = 0; x < grid.width; x += stride) {
      const value = depth[y * grid.width + x] ?? 0;
      if (value < threshold) continue;
      if (features.length >= maximumFeatures) break sampling;
      const coordinates = gridToLongitudeLatitude(grid.bounds, grid.width, grid.height, x, y);
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates },
        properties: { depth_m: value, grid_x: x, grid_y: y }
      });
    }
  }
  return {
    type: 'FeatureCollection',
    features,
    metadata: { threshold_m: threshold, sampling_stride: stride, source_grid: grid.id }
  };
}

export function exportWetExtent(grid: DemGrid, depth: Float32Array, thresholdMeters = 0.01) {
  if (depth.length !== grid.width * grid.height) {
    throw new Error('Depth count does not match the DEM dimensions.');
  }
  const rings = traceWetCellRings(grid, depth, thresholdMeters).map((ring) =>
    ring.map(([x, y]) => gridEdgeToLongitudeLatitude(grid, x, y))
  );
  const polygons = groupPolygonRings(rings);
  const features = polygons.length
    ? [
        {
          type: 'Feature',
          geometry:
            polygons.length === 1
              ? { type: 'Polygon', coordinates: polygons[0] }
              : { type: 'MultiPolygon', coordinates: polygons },
          properties: { threshold_m: thresholdMeters, method: 'wet-cell boundary polygonization' }
        }
      ]
    : [];
  return { type: 'FeatureCollection', features };
}

export function exportSimulationReport(
  grid: DemGrid,
  statistics: SimulationStatistics,
  thresholdMeters: number,
  context: SimulationReportContext = {}
) {
  const injectedVolume = context.sourceHistory
    ? context.sourceHistory.reduce((total, source) => total + source.injectedVolumeCubicMeters, 0)
    : null;
  return {
    schema: 'https://openfloodlab.org/schemas/simulation-report/v2',
    generated_at: new Date().toISOString(),
    model: 'OpenFloodLab hydraulic-head flux grid prototype',
    software: context.software ?? { name: 'OpenFloodLab', version: 'unknown' },
    solver: {
      implementation: context.solverImplementation ?? 'unknown',
      parameters: context.solverParameters ?? null
    },
    dem: {
      id: grid.id,
      width: grid.width,
      height: grid.height,
      bounds: grid.bounds,
      encoding: context.dem?.encoding ?? null,
      sha256: context.dem?.sha256 ?? null,
      source: context.dem?.source ?? null
    },
    sources: context.sourceHistory ?? [],
    threshold_m: thresholdMeters,
    statistics,
    mass_balance: {
      injected_volume_m3: injectedVolume,
      stored_volume_m3: statistics.waterVolumeCubicMeters,
      unaccounted_volume_m3:
        injectedVolume === null ? null : injectedVolume - statistics.waterVolumeCubicMeters,
      note: 'Unaccounted volume combines configured boundary drainage, depth clipping, and numerical error.'
    },
    disclaimer:
      'Research and education output only. Not an operational forecast or warning product.'
  };
}

type GridPoint = readonly [x: number, y: number];

interface BoundaryEdge {
  readonly start: GridPoint;
  readonly end: GridPoint;
  readonly direction: 0 | 1 | 2 | 3;
  used: boolean;
}

function traceWetCellRings(
  grid: DemGrid,
  depth: Float32Array,
  thresholdMeters: number
): GridPoint[][] {
  const wet = (x: number, y: number) =>
    x >= 0 &&
    x < grid.width &&
    y >= 0 &&
    y < grid.height &&
    (depth[y * grid.width + x] ?? 0) >= thresholdMeters;
  const edges: BoundaryEdge[] = [];
  const add = (start: GridPoint, end: GridPoint, direction: 0 | 1 | 2 | 3) =>
    edges.push({ start, end, direction, used: false });

  for (let y = 0; y < grid.height; y += 1) {
    for (let x = 0; x < grid.width; x += 1) {
      if (!wet(x, y)) continue;
      if (!wet(x, y - 1)) add([x, y], [x + 1, y], 0);
      if (!wet(x + 1, y)) add([x + 1, y], [x + 1, y + 1], 1);
      if (!wet(x, y + 1)) add([x + 1, y + 1], [x, y + 1], 2);
      if (!wet(x - 1, y)) add([x, y + 1], [x, y], 3);
    }
  }

  const byStart = new Map<string, number[]>();
  edges.forEach((edge, index) => {
    const key = pointKey(edge.start);
    const values = byStart.get(key) ?? [];
    values.push(index);
    byStart.set(key, values);
  });

  const rings: GridPoint[][] = [];
  for (let startIndex = 0; startIndex < edges.length; startIndex += 1) {
    const first = edges[startIndex];
    if (!first || first.used) continue;
    const ring: GridPoint[] = [first.start];
    let edge = first;
    for (let guard = 0; guard <= edges.length; guard += 1) {
      edge.used = true;
      ring.push(edge.end);
      if (samePoint(edge.end, first.start)) break;
      const candidates = (byStart.get(pointKey(edge.end)) ?? [])
        .map((index) => edges[index])
        .filter((candidate): candidate is BoundaryEdge => !!candidate && !candidate.used);
      const next = selectBoundaryContinuation(edge.direction, candidates);
      if (!next) throw new Error('Wet-cell boundary could not be closed.');
      edge = next;
    }
    if (ring.length >= 4 && samePoint(ring[0]!, ring[ring.length - 1]!)) rings.push(ring);
  }
  return rings;
}

function selectBoundaryContinuation(
  incoming: BoundaryEdge['direction'],
  candidates: BoundaryEdge[]
): BoundaryEdge | undefined {
  const turnPriority = [1, 0, 3, 2];
  return candidates.sort(
    (left, right) =>
      turnPriority.indexOf((left.direction - incoming + 4) % 4) -
      turnPriority.indexOf((right.direction - incoming + 4) % 4)
  )[0];
}

function gridEdgeToLongitudeLatitude(grid: DemGrid, x: number, y: number): number[] {
  const [west, south, east, north] = grid.bounds;
  const normalizedX = Math.max(0, Math.min(1, (x - 0.5) / (grid.width - 1)));
  const normalizedY = Math.max(0, Math.min(1, (y - 0.5) / (grid.height - 1)));
  return [west + normalizedX * (east - west), north - normalizedY * (north - south)];
}

function groupPolygonRings(rings: number[][][]): number[][][][] {
  const outerRings = rings.filter((ring) => signedArea(ring) < 0);
  const holeRings = rings.filter((ring) => signedArea(ring) >= 0);
  const polygons = outerRings.map((ring) => [ring.slice().reverse()]);

  for (const hole of holeRings) {
    const point = hole[0];
    const containers = outerRings
      .map((outer, index) => ({ index, area: Math.abs(signedArea(outer)), outer }))
      .filter(({ outer }) => !!point && pointInRing(point, outer))
      .sort((left, right) => left.area - right.area);
    const container = containers[0];
    if (container) polygons[container.index]?.push(hole.slice().reverse());
    else polygons.push([hole]);
  }
  return polygons;
}

function signedArea(ring: number[][]): number {
  let area = 0;
  for (let index = 0; index < ring.length - 1; index += 1) {
    const current = ring[index]!;
    const next = ring[index + 1]!;
    area += (current[0] ?? 0) * (next[1] ?? 0) - (next[0] ?? 0) * (current[1] ?? 0);
  }
  return area * 0.5;
}

function pointInRing(point: number[], ring: number[][]): boolean {
  let inside = false;
  for (let current = 0, previous = ring.length - 1; current < ring.length; previous = current++) {
    const a = ring[current]!;
    const b = ring[previous]!;
    const crosses =
      a[1]! > point[1]! !== b[1]! > point[1]! &&
      point[0]! < ((b[0]! - a[0]!) * (point[1]! - a[1]!)) / (b[1]! - a[1]!) + a[0]!;
    if (crosses) inside = !inside;
  }
  return inside;
}

function pointKey([x, y]: GridPoint): string {
  return `${x},${y}`;
}

function samePoint(left: GridPoint, right: GridPoint): boolean {
  return left[0] === right[0] && left[1] === right[1];
}
